import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  IInvestmentListResponse,
  IInvestmentDetails,
  IInvestmentDetailsResponse,
  IInvestmentReasonPayload,
  IInvestmentCreationRequest,
  IInvestorsLookupResponse,
  IOfferingPaymentOptionsResponse,
} from '../../interfaces/investment/investment';
import { InvestmentStatus } from '../../enums/investment-status';
import { API_ENDPOINTS } from '../../utils/api-endpoints.const';
import { ApiResponse } from '../../interfaces/shared/fund/api-response';

/** Page size scanned when checking for a succeeded investment. */
const SUCCEEDED_INVESTMENT_SCAN_SIZE = 100;

@Injectable({
  providedIn: 'root',
})
export class FundInvestments {
  private readonly http = inject(HttpClient);

  getInvestmentsList(
    fundUuid: string,
    page: number,
    perPage: number,
    offeringUuid?: string
  ): Observable<IInvestmentListResponse> {
    const params: Record<string, string | number> = {
      page,
      per_page: perPage,
    };
    if (offeringUuid) {
      params['offering_uuid'] = offeringUuid;
    }
    return this.http.get<IInvestmentListResponse>(
      API_ENDPOINTS.FUND_INVESTMENTS(fundUuid),
      { params }
    );
  }

  /**
   * Whether the fund has at least one succeeded investment — the gate for the
   * Unit Management tab and adding restricted units. Reads the investments list
   * and looks for a `succeeded` status; any load error resolves to `false` so
   * callers stay gated rather than break.
   */
  hasSucceededInvestment(fundUuid: string): Observable<boolean> {
    return this.getInvestmentsList(
      fundUuid,
      1,
      SUCCEEDED_INVESTMENT_SCAN_SIZE
    ).pipe(
      map(response =>
        (response.data ?? []).some(
          investment => investment.status?.value === InvestmentStatus.SUCCEEDED
        )
      ),
      catchError(() => of(false))
    );
  }

  /**
   * Investors lookup. Pass `hasSuccessfulInvestmentInFund` (a fund UUID) to
   * restrict the list to investors with a succeeded investment in that fund —
   * used by the create-restricted-unit form.
   */
  getInvestorsList(
    hasSuccessfulInvestmentInFund?: string
  ): Observable<IInvestorsLookupResponse> {
    const params = hasSuccessfulInvestmentInFund
      ? { has_successful_investment_in_fund: hasSuccessfulInvestmentInFund }
      : undefined;
    return this.http.get<IInvestorsLookupResponse>(
      `${API_ENDPOINTS.LOOKUPS}/investors`,
      { params }
    );
  }

  getPaymentOptionsForOffering(
    fundUuid: string,
    offeringUuid: string
  ): Observable<IOfferingPaymentOptionsResponse> {
    return this.http.get<IOfferingPaymentOptionsResponse>(
      API_ENDPOINTS.FUND_OFFERING_PAYMENT_OPTIONS(fundUuid, offeringUuid)
    );
  }

  createInvestment(
    fundUuid: string,
    offeringUuid: string,
    payload: IInvestmentCreationRequest
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      API_ENDPOINTS.FUND_OFFERING_INVESTMENTS(fundUuid, offeringUuid),
      payload
    );
  }

  getInvestmentDetails(
    fundUuid: string,
    investmentUuid: string
  ): Observable<IInvestmentDetails> {
    return this.http
      .get<IInvestmentDetailsResponse>(
        API_ENDPOINTS.FUND_INVESTMENT_DETAILS(fundUuid, investmentUuid)
      )
      .pipe(map(response => response.data));
  }

  approveInvestment(
    fundUuid: string,
    investmentUuid: string
  ): Observable<unknown> {
    return this.http.post(
      API_ENDPOINTS.FUND_INVESTMENT_APPROVE(fundUuid, investmentUuid),
      {}
    );
  }

  rejectInvestment(
    fundUuid: string,
    investmentUuid: string,
    payload: IInvestmentReasonPayload
  ): Observable<unknown> {
    return this.http.post(
      API_ENDPOINTS.FUND_INVESTMENT_REJECT(fundUuid, investmentUuid),
      payload
    );
  }

  reuploadInvestment(
    fundUuid: string,
    investmentUuid: string,
    payload: IInvestmentReasonPayload
  ): Observable<unknown> {
    return this.http.post(
      API_ENDPOINTS.FUND_INVESTMENT_REUPLOAD(fundUuid, investmentUuid),
      payload
    );
  }
}
